import { useEffect, useRef, useState } from "react";
import {
  cartItemToRewardMatchProduct,
  computeRewardBarState,
  getMatchingCartQuantity,
  isSpecificProductsRewardMode,
  REWARD_TIER_MATCH_MIN_QTY_DEFAULT,
  REWARD_TIER_TYPE,
  resolveRewardProductVariantId,
} from "../../shared/reward-bar.js";
import { matchOfferWhen } from "../../shared/resolve-effective-offer.js";
import {
  buildRewardProductLineProperties,
  CHECKOUT_TRACKING_KEYS,
  extractShopifyNumericId,
  rewardLineHasRevenueTracking,
} from "../../shared/checkout-tracking.js";
import cartService from "../services/cart.service";
import { showCartAddError } from "./cart-add-toast";
import { getShopifyCartUrl } from "./shopify-cart-url";
import { syncStorefrontCartUi } from "./sync-storefront-cart-after-add";
import {
  buildRewardBarContextCacheKey,
  preloadRewardBarCollectionMembership,
  preloadRewardBarProductMeta,
  resolveRewardBarGiftPools,
  rewardBarSettingNeedsStorefrontResolve,
} from "./reward-bar-storefront-resolve";

/** Quiet mutations during reward sync — one theme UI sync at the end. */
const QUIET_CHANGE = { syncCartUi: false };
const QUIET_ADD = {
  attachTracking: false,
  syncCartAfterAdd: false,
  suppressModalOnCartAddRefire: true,
};

/** Serialize reward syncs so rapid ATC cannot grant duplicate free gifts. */
let rewardSyncChain = Promise.resolve();

/** Shared storefront resolve cache (collection When + dynamic gift pools). */
let rewardRuntimeContext = {
  key: "",
  collectionProductIdsByKey: null,
  productMetaById: null,
  giftPoolsByTierId: new Map(),
};
let rewardRuntimeContextPromise = null;

export function getRewardBarMatchOptions() {
  return {
    collectionProductIdsByKey:
      rewardRuntimeContext.collectionProductIdsByKey || null,
    productMetaById: rewardRuntimeContext.productMetaById || null,
  };
}

export async function ensureRewardBarRuntimeContext(setting) {
  if (!setting) {
    return rewardRuntimeContext;
  }
  const key = buildRewardBarContextCacheKey(setting);
  if (rewardRuntimeContext.key === key) {
    return rewardRuntimeContext;
  }
  // Wait out an in-flight resolve for a *different* key, then rebuild.
  // Returning the old promise here would leave gift pools stale when
  // When / Products-to-offer change mid-session (admin preview / tests).
  if (rewardRuntimeContextPromise) {
    await rewardRuntimeContextPromise.catch(() => null);
    if (rewardRuntimeContext.key === key) {
      return rewardRuntimeContext;
    }
  }

  const task = (async () => {
    const needsResolve = rewardBarSettingNeedsStorefrontResolve(setting);
    let collectionProductIdsByKey = Object.create(null);
    let productMetaById = Object.create(null);
    let giftPoolsByTierId = new Map();
    if (needsResolve) {
      collectionProductIdsByKey =
        await preloadRewardBarCollectionMembership(setting);
      productMetaById = await preloadRewardBarProductMeta(setting);
      giftPoolsByTierId = await resolveRewardBarGiftPools(setting);
    } else {
      // Still hydrate static specific_product pools into the map for callers.
      giftPoolsByTierId = await resolveRewardBarGiftPools(setting);
    }
    rewardRuntimeContext = {
      key,
      collectionProductIdsByKey,
      productMetaById,
      giftPoolsByTierId,
    };
    return rewardRuntimeContext;
  })();

  rewardRuntimeContextPromise = task;
  try {
    return await task;
  } finally {
    if (rewardRuntimeContextPromise === task) {
      rewardRuntimeContextPromise = null;
    }
  }
}

function getTierRewardProducts(tier) {
  const cached = rewardRuntimeContext.giftPoolsByTierId?.get(String(tier?.id));
  if (Array.isArray(cached) && cached.length) {
    return cached;
  }
  const offerType = tier?.products_to_offer?.type || "specific_product";
  // Collection / all_except / text filters resolve via ensureRewardBarRuntimeContext.
  // Do not fall back to leftover tier.products from a prior specific_product config.
  if (offerType !== "specific_product") {
    return Array.isArray(cached) ? cached : [];
  }
  if (Array.isArray(tier?.products) && tier.products.length) {
    return tier.products;
  }
  if (tier?.product) return [tier.product];
  const offerValue = tier?.products_to_offer?.value;
  if (Array.isArray(offerValue) && offerValue.length) {
    return offerValue.filter((product) => product && (product.id || product.title));
  }
  return [];
}

/** Public gift-pool lookup (collection / all_except resolved via runtime context). */
export function getRewardProductsForTier(tier) {
  return getTierRewardProducts(tier);
}

/**
 * Auto-claim only when there is a single gift choice.
 * Multiple products → shopper must pick via FreeGiftPickerSheet.
 */
function tierAllowsAutoClaim(tier) {
  return getTierRewardProducts(tier).length <= 1;
}

function computeRewardState(cart, setting) {
  return computeRewardBarState(
    cart,
    setting,
    undefined,
    getRewardBarMatchOptions()
  );
}

/** Respect merchant "Number of products" limit when granting/showing rewards. */
function getActiveTierRewardProducts(tier) {
  const products = getTierRewardProducts(tier);
  const limit = Math.max(
    1,
    Number(tier?.products_to_offer_limit) || products.length || 1
  );
  return products.slice(0, limit);
}

export function getLineRewardTierId(item) {
  const props = item?.properties;
  if (!props || typeof props !== "object") return "";
  return String(props[CHECKOUT_TRACKING_KEYS.REWARD_TIER_ID] || "");
}

export function rewardLineAlreadyInCart(cart, tierId, variantId) {
  return (cart?.items || []).some(
    (item) =>
      String(item.variant_id) === String(variantId) &&
      getLineRewardTierId(item) === String(tierId)
  );
}

function findRewardVariantLine(cart, tierId, variantId) {
  return (cart?.items || []).find(
    (item) =>
      String(item.variant_id) === String(variantId) &&
      getLineRewardTierId(item) === String(tierId)
  );
}

function findUntaggedVariantLine(cart, variantId) {
  return (cart?.items || []).find(
    (item) =>
      String(item.variant_id) === String(variantId) &&
      !getLineRewardTierId(item)
  );
}

function findPoolProductForCartLine(tier, pool, item) {
  const lineVariantId = String(item?.variant_id || "");
  const lineProductId = extractShopifyNumericId(
    item?.product_id || item?.productId || ""
  );
  return (
    (pool || []).find((product) => {
      const variantId = resolveRewardProductVariantId(product, tier);
      if (variantId && lineVariantId && String(variantId) === lineVariantId) {
        return true;
      }
      const productId = extractShopifyNumericId(product?.id);
      return Boolean(
        productId && lineProductId && productId === lineProductId
      );
    }) || null
  );
}

/**
 * Retagging 1 unit as a free gift removes it from When matching (tagged lines
 * are skipped). True when When would still be achieved after retag.
 */
function canClaimUnitWithoutBreakingWhen(cart, tier, item) {
  const when = tier?.when;
  if (!when?.type) return true;
  const minQty = Math.max(
    REWARD_TIER_MATCH_MIN_QTY_DEFAULT,
    Number(tier.matchMinimumQuantity) || REWARD_TIER_MATCH_MIN_QTY_DEFAULT
  );
  const matchOptions = getRewardBarMatchOptions();
  const product = cartItemToRewardMatchProduct(item, matchOptions);
  if (!product || !matchOfferWhen(when, product, matchOptions)) return true;
  const matchedQty = getMatchingCartQuantity(cart, tier, matchOptions);
  return matchedQty - 1 >= minQty;
}

/**
 * Next cart line that can auto-consume one free-gift slot (PDP / qty+).
 * Prefer gift-pool lines that are not When-qualifiers; grant decides retag vs add.
 */
function findNextAutoClaimSelection(cart, tier) {
  const pool = getTierRewardProducts(tier);
  if (!pool.length) return null;

  const candidates = [];
  for (const item of cart?.items || []) {
    if (getLineRewardTierId(item)) continue;
    const variantId = String(item?.variant_id || "");
    if (!variantId) continue;
    if (rewardLineAlreadyInCart(cart, tier.id, variantId)) continue;

    const product = findPoolProductForCartLine(tier, pool, item);
    if (!product) continue;

    const matchOptions = getRewardBarMatchOptions();
    const whenProduct = cartItemToRewardMatchProduct(item, matchOptions);
    const matchesWhen = Boolean(
      tier?.when?.type &&
        whenProduct &&
        matchOfferWhen(tier.when, whenProduct, matchOptions)
    );
    candidates.push({ product, variantId, matchesWhen });
  }

  if (!candidates.length) return null;
  // Safe (non-When) gift lines first so unlock qty is preserved when possible.
  candidates.sort((a, b) => Number(a.matchesWhen) - Number(b.matchesWhen));
  return {
    product: candidates[0].product,
    variantId: candidates[0].variantId,
  };
}

function tierHasAutoClaimWork(cart, tier) {
  if (!tier || tier.rewardType !== REWARD_TIER_TYPE.PRODUCT) return false;
  if (!tierAllowsAutoClaim(tier)) return false;
  const limit = Math.max(1, Number(tier.products_to_offer_limit) || 1);
  if (countRewardLinesForTier(cart, tier.id) >= limit) return false;
  return Boolean(findNextAutoClaimSelection(cart, tier));
}

/**
 * Untagged gift-pool line that auto-claim is about to convert — use for
 * optimistic FREE / loading UI so the paid price never flashes.
 */
export function isPendingAutoClaimGiftLine(item, cart, setting) {
  if (!item || !cart || !setting) return false;
  if (!isSpecificProductsRewardMode(setting)) return false;
  if (getLineRewardTierId(item)) return false;

  const state = computeRewardState(cart, setting);
  for (const tier of state.tiers) {
    if (tier.rewardType !== REWARD_TIER_TYPE.PRODUCT) continue;
    if (!state.achievedTierIds.has(tier.id)) continue;
    if (!tierAllowsAutoClaim(tier)) continue;
    const limit = Math.max(1, Number(tier.products_to_offer_limit) || 1);
    if (countRewardLinesForTier(cart, tier.id) >= limit) continue;
    const pool = getTierRewardProducts(tier);
    if (!pool.length) continue;
    if (!findPoolProductForCartLine(tier, pool, item)) continue;
    return true;
  }
  return false;
}

/**
 * Specific-products: auto-tag eligible free-gift lines already in the cart
 * (PDP add / qty+) up to products_to_offer_limit. Extra units stay paid.
 */
async function autoClaimSpecificProductFreeGifts(
  cart,
  setting,
  trackingContext = {}
) {
  if (!isSpecificProductsRewardMode(setting)) {
    return { changed: false, cart };
  }

  let workingCart = cart;
  let changed = false;
  const state = computeRewardState(workingCart, setting);

  for (const tier of state.tiers) {
    if (tier.rewardType !== REWARD_TIER_TYPE.PRODUCT) continue;
    if (!state.achievedTierIds.has(tier.id)) continue;
    // Multiple gift choices → picker only (never auto-add).
    if (!tierAllowsAutoClaim(tier)) continue;

    const limit = Math.max(1, Number(tier.products_to_offer_limit) || 1);

    for (let i = 0; i < limit; i += 1) {
      try {
        workingCart = await fetchCartJson();
      } catch (_) {}

      // Re-check unlock after each claim (When qty may drop).
      const liveState = computeRewardState(workingCart, setting);
      if (!liveState.achievedTierIds.has(tier.id)) break;

      if (countRewardLinesForTier(workingCart, tier.id) >= limit) break;

      const selection = findNextAutoClaimSelection(workingCart, tier);
      if (!selection) break;

      await grantRewardProductLine(
        workingCart,
        tier,
        selection.product,
        selection.variantId,
        trackingContext
      );
      changed = true;
      try {
        workingCart = await fetchCartJson();
      } catch (_) {}
    }
  }

  return { changed, cart: workingCart };
}

function getActiveRewardVariantIdSet(tier) {
  const ids = new Set();
  // Use full configured product list for orphan cleanup (not offer-limit slice)
  for (const product of getTierRewardProducts(tier)) {
    const variantId = resolveRewardProductVariantId(product, tier);
    if (variantId) ids.add(String(variantId));
  }
  return ids;
}

/**
 * Remove reward-tagged lines that no longer match the tier's selected products.
 */
async function removeOrphanedRewardProductLines(cart, setting) {
  if (!cart || !setting) return { changed: false, cart };

  const productTiers = (setting.tiers || []).filter(
    (tier) => tier?.rewardType === REWARD_TIER_TYPE.PRODUCT
  );
  if (!productTiers.length) return { changed: false, cart };

  const tierById = new Map(
    productTiers.map((tier) => [String(tier.id), tier])
  );
  const activeVariantsByTier = new Map(
    productTiers.map((tier) => [String(tier.id), getActiveRewardVariantIdSet(tier)])
  );

  let workingCart = cart;
  let changed = false;

  for (const item of [...(workingCart.items || [])]) {
    const tierId = getLineRewardTierId(item);
    if (!tierId || !tierById.has(tierId)) continue;

    const activeVariants = activeVariantsByTier.get(tierId) || new Set();
    const variantId = String(item?.variant_id || "");
    if (activeVariants.has(variantId)) continue;

    if (!item?.key) continue;
    await cartService.changeLineItem({
      id: item.key,
      quantity: 0,
      ...QUIET_CHANGE,
    });
    changed = true;
    workingCart = await fetchCartJson();
  }

  return { changed, cart: workingCart };
}

/**
 * Keep exactly one reward unit per tier+variant (dedupe race-granted gifts).
 */
async function dedupeExcessRewardProductLines(cart, setting) {
  if (!cart || !setting) return { changed: false, cart };

  const productTiers = (setting.tiers || []).filter(
    (tier) => tier?.rewardType === REWARD_TIER_TYPE.PRODUCT
  );
  if (!productTiers.length) return { changed: false, cart };

  const productTierIds = new Set(productTiers.map((tier) => String(tier.id)));
  let workingCart = cart;
  let changed = false;

  const groups = new Map();
  for (const item of workingCart.items || []) {
    const tierId = getLineRewardTierId(item);
    if (!tierId || !productTierIds.has(String(tierId))) continue;
    const variantId = String(item?.variant_id || "");
    if (!variantId) continue;
    const key = `${tierId}:${variantId}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }

  for (const lines of groups.values()) {
    if (!lines.length) continue;
    // Keep the first line at qty 1; remove the rest.
    const [keep, ...extras] = lines;
    if (Number(keep.quantity) > 1 && keep.key) {
      await cartService.changeLineItem({
        id: keep.key,
        quantity: 1,
        ...QUIET_CHANGE,
      });
      changed = true;
      workingCart = await fetchCartJson();
    }
    for (const extra of extras) {
      if (!extra?.key) continue;
      await cartService.changeLineItem({
        id: extra.key,
        quantity: 0,
        ...QUIET_CHANGE,
      });
      changed = true;
      workingCart = await fetchCartJson();
    }
  }

  return { changed, cart: workingCart };
}

/**
 * Remove free-gift / reward product lines once their tier is no longer achieved
 * (e.g. shopper cleared other cart items).
 */
async function removeUnachievedRewardProductLines(cart, setting) {
  if (!cart || !setting) return { changed: false, cart };

  const productTiers = (setting.tiers || []).filter(
    (tier) => tier?.rewardType === REWARD_TIER_TYPE.PRODUCT
  );
  if (!productTiers.length) return { changed: false, cart };

  const productTierIds = new Set(productTiers.map((tier) => String(tier.id)));
  let workingCart = cart;
  let changed = false;

  // Recompute after each removal so thresholds stay accurate.
  while (true) {
    const state = computeRewardState(workingCart, setting);
    const stale = (workingCart.items || []).find((item) => {
      const tierId = getLineRewardTierId(item);
      if (!tierId || !productTierIds.has(String(tierId))) return false;
      return !state.achievedTierIds.has(tierId);
    });
    if (!stale?.key) break;

    await cartService.changeLineItem({
      id: stale.key,
      quantity: 0,
      ...QUIET_CHANGE,
    });
    changed = true;
    workingCart = await fetchCartJson();
  }

  return { changed, cart: workingCart };
}

function resolveTrackingContext(trackingContext = {}) {
  const shop =
    trackingContext.shop ||
    (typeof window !== "undefined"
      ? window?.Shopify?.shop || window.location.hostname
      : "");
  return {
    shop,
    popup: trackingContext.popup || null,
  };
}

function buildLineProperties({
  tier,
  product,
  variantId,
  existingProperties,
  trackingContext,
}) {
  const ctx = resolveTrackingContext(trackingContext);
  return buildRewardProductLineProperties({
    shop: ctx.shop,
    popup: ctx.popup,
    product,
    variantId,
    tierId: tier.id,
    existingProperties,
  });
}

async function fetchCartJson() {
  const response = await fetch(getShopifyCartUrl("cart.js"));
  if (!response.ok) throw new Error("Failed to refresh cart");
  return response.json();
}

async function upgradeRewardLineRevenueTracking(
  cart,
  tier,
  product,
  variantId,
  trackingContext
) {
  if (!resolveTrackingContext(trackingContext).popup) return false;

  const rewardLine = findRewardVariantLine(cart, tier.id, variantId);
  if (!rewardLine?.key) return false;
  if (rewardLineHasRevenueTracking(rewardLine.properties)) return false;

  const properties = buildLineProperties({
    tier,
    product,
    variantId,
    existingProperties: rewardLine.properties,
    trackingContext,
  });

  await cartService.changeLineItem({
    id: rewardLine.key,
    quantity: Number(rewardLine.quantity) || 1,
    properties,
    ...QUIET_CHANGE,
  });
  return true;
}

/**
 * Grant one discounted reward unit for a product tier.
 */
export async function grantRewardProductLine(
  cart,
  tier,
  product,
  variantId,
  trackingContext
) {
  // Re-check against a fresh cart when possible — callers may pass a stale snapshot.
  if (rewardLineAlreadyInCart(cart, tier.id, variantId)) {
    return upgradeRewardLineRevenueTracking(
      cart,
      tier,
      product,
      variantId,
      trackingContext
    );
  }

  const untaggedLine = findUntaggedVariantLine(cart, variantId);
  const rewardProperties = buildLineProperties({
    tier,
    product,
    variantId,
    existingProperties: untaggedLine?.properties,
    trackingContext,
  });

  // Retag only when the line is not required to keep When unlocked.
  // Otherwise add a separate free unit (PDP gift that also qualifies When).
  const safeToRetag =
    Boolean(untaggedLine?.key) &&
    Number(untaggedLine.quantity) === 1 &&
    canClaimUnitWithoutBreakingWhen(cart, tier, untaggedLine);

  if (safeToRetag) {
    await cartService.changeLineItem({
      id: untaggedLine.key,
      quantity: 1,
      properties: rewardProperties,
      ...QUIET_CHANGE,
    });
    return true;
  }

  if (untaggedLine?.key && Number(untaggedLine.quantity) > 1) {
    await cartService.changeLineItem({
      id: untaggedLine.key,
      quantity: Number(untaggedLine.quantity) - 1,
      ...QUIET_CHANGE,
    });
    await cartService.addToCart(
      [
        {
          id: Number(variantId),
          quantity: 1,
          properties: rewardProperties,
        },
      ],
      QUIET_ADD
    );
    return true;
  }

  await cartService.addToCart(
    [
      {
        id: Number(variantId),
        quantity: 1,
        properties: rewardProperties,
      },
    ],
    QUIET_ADD
  );
  return true;
}

/**
 * Core reward sync (no mutex). Always starts from a fresh /cart.js when possible.
 */
async function syncRewardProductCartLinesInner(
  cart,
  setting,
  trackingContext = {}
) {
  if (!setting) return null;

  try {
    await ensureRewardBarRuntimeContext(setting);
  } catch (_) {}

  let workingCart = cart;
  try {
    workingCart = await fetchCartJson();
  } catch (_) {
    if (!cart) return null;
    workingCart = cart;
  }

  let changed = false;

  const orphanResult = await removeOrphanedRewardProductLines(workingCart, setting);
  if (orphanResult.changed) {
    changed = true;
    workingCart = orphanResult.cart;
  }

  const unachievedResult = await removeUnachievedRewardProductLines(
    workingCart,
    setting
  );
  if (unachievedResult.changed) {
    changed = true;
    workingCart = unachievedResult.cart;
  }

  const dedupeResult = await dedupeExcessRewardProductLines(workingCart, setting);
  if (dedupeResult.changed) {
    changed = true;
    workingCart = dedupeResult.cart;
  }

  // Specific-products: auto-claim free slots from cart lines (PDP / qty+),
  // then leave remaining slots for the picker sheet.
  if (isSpecificProductsRewardMode(setting)) {
    const claimResult = await autoClaimSpecificProductFreeGifts(
      workingCart,
      setting,
      trackingContext
    );
    if (claimResult.changed) {
      changed = true;
      workingCart = claimResult.cart;
    }

    const claimDedupe = await dedupeExcessRewardProductLines(
      workingCart,
      setting
    );
    if (claimDedupe.changed) {
      changed = true;
      workingCart = claimDedupe.cart;
    }

    if (changed) {
      await syncStorefrontCartUi(null, {
        cartSnapshot: workingCart,
        fetchCart: false,
        refreshDrawer: false,
      });
      return workingCart;
    }
    return null;
  }

  const state = computeRewardState(workingCart, setting);
  const productTiers = state.tiers.filter(
    (tier) =>
      tier.rewardType === REWARD_TIER_TYPE.PRODUCT &&
      state.achievedTierIds.has(tier.id)
  );

  if (!productTiers.length) {
    if (changed) {
      await syncStorefrontCartUi(null, {
        cartSnapshot: workingCart,
        fetchCart: false,
        refreshDrawer: false,
      });
      return workingCart;
    }
    return null;
  }

  for (const tier of productTiers) {
    for (const product of getActiveTierRewardProducts(tier)) {
      const variantId = resolveRewardProductVariantId(product, tier);
      if (!variantId) continue;

      // Fresh cart before each grant — prevents double-add on rapid ATC.
      try {
        workingCart = await fetchCartJson();
      } catch (_) {}

      const needsGrant = !rewardLineAlreadyInCart(workingCart, tier.id, variantId);
      const needsTrackingUpgrade =
        rewardLineAlreadyInCart(workingCart, tier.id, variantId) &&
        !rewardLineHasRevenueTracking(
          findRewardVariantLine(workingCart, tier.id, variantId)?.properties
        ) &&
        Boolean(resolveTrackingContext(trackingContext).popup);

      if (!needsGrant && !needsTrackingUpgrade) continue;

      await grantRewardProductLine(
        workingCart,
        tier,
        product,
        variantId,
        trackingContext
      );
      changed = true;
      workingCart = await fetchCartJson();
    }
  }

  // Collapse any duplicates created by overlapping add.js responses.
  const finalDedupe = await dedupeExcessRewardProductLines(workingCart, setting);
  if (finalDedupe.changed) {
    changed = true;
    workingCart = finalDedupe.cart;
  }

  if (!changed) return null;

  try {
    await syncStorefrontCartUi(null, {
      cartSnapshot: workingCart,
      fetchCart: false,
      refreshDrawer: false,
    });
  } catch (_) {}

  return workingCart;
}

/**
 * Ensure achieved product-tier rewards are tagged for discount + revenue tracking.
 * Serialized — concurrent callers run one-after-another against a fresh cart.
 *
 * @returns {Promise<object | null>} Updated cart from /cart.js when cart changed.
 */
export async function syncRewardProductCartLines(
  cart,
  setting,
  trackingContext = {}
) {
  if (!setting) return null;

  const task = rewardSyncChain.then(() =>
    syncRewardProductCartLinesInner(cart, setting, trackingContext)
  );
  // Keep the queue moving even when a sync fails.
  rewardSyncChain = task.catch(() => null);
  return task;
}

function countRewardLinesForTier(cart, tierId) {
  return (cart?.items || []).filter(
    (item) => getLineRewardTierId(item) === String(tierId)
  ).length;
}

/**
 * Achieved specific-products free-gift tiers that still need shopper selection.
 */
export function getTiersNeedingGiftSelection(cart, setting, options = {}) {
  if (!isSpecificProductsRewardMode(setting)) return [];
  const state = computeRewardBarState(cart, setting, undefined, {
    ...getRewardBarMatchOptions(),
    ...options,
  });
  return state.tiers.filter((tier) => {
    if (tier.rewardType !== REWARD_TIER_TYPE.PRODUCT) return false;
    if (!state.achievedTierIds.has(tier.id)) return false;
    const products = getTierRewardProducts(tier);
    if (!products.length) return false;
    const limit = Math.max(
      1,
      Number(tier.products_to_offer_limit) || 1
    );
    const selected = countRewardLinesForTier(cart, tier.id);
    return selected < limit;
  });
}

/**
 * Unlocked tiers where the shopper already picked a gift and can switch
 * (pool has more than one product).
 */
export function getTiersAllowingGiftChange(cart, setting, options = {}) {
  if (!isSpecificProductsRewardMode(setting)) return [];
  const state = computeRewardBarState(cart, setting, undefined, {
    ...getRewardBarMatchOptions(),
    ...options,
  });
  return state.tiers.filter((tier) => {
    if (tier.rewardType !== REWARD_TIER_TYPE.PRODUCT) return false;
    if (!state.achievedTierIds.has(tier.id)) return false;
    const products = getTierRewardProducts(tier);
    if (products.length <= 1) return false;
    return countRewardLinesForTier(cart, tier.id) > 0;
  });
}

/**
 * Tiers to show in the free-gift sheet: prefer unfilled slots, else change.
 * @returns {Array<object & { pickerMode?: 'select'|'change' }>}
 */
export function getTiersForFreeGiftPicker(cart, setting, options = {}) {
  const needing = getTiersNeedingGiftSelection(cart, setting, options);
  if (needing.length) {
    return needing.map((tier) => ({ ...tier, pickerMode: "select" }));
  }
  return getTiersAllowingGiftChange(cart, setting, options).map((tier) => ({
    ...tier,
    pickerMode: "change",
  }));
}

export function getRewardTierSelectableProducts(tier) {
  return getTierRewardProducts(tier);
}

async function removeRewardLinesForTier(cart, tierId) {
  let workingCart = cart;
  let changed = false;
  const id = String(tierId);
  for (const item of [...(workingCart?.items || [])]) {
    if (getLineRewardTierId(item) !== id || !item?.key) continue;
    await cartService.changeLineItem({
      id: item.key,
      quantity: 0,
      ...QUIET_CHANGE,
    });
    changed = true;
    try {
      workingCart = await fetchCartJson();
    } catch (_) {}
  }
  return { changed, cart: workingCart };
}

/**
 * Grant shopper-selected free gifts for specific-products mode.
 * @param {Array<{ tierId: string, product: object, variantId: string }>} selections
 * @param {{ replace?: boolean }} [options] When true, clear existing reward
 *   lines for the selection tier(s) before granting (change-gift flow).
 */
export async function grantSelectedRewardGifts(
  cart,
  setting,
  selections = [],
  trackingContext = {},
  options = {}
) {
  if (!setting || !Array.isArray(selections) || !selections.length) {
    return null;
  }

  const replace = Boolean(options.replace);

  const task = rewardSyncChain.then(async () => {
    let workingCart = cart;
    try {
      workingCart = await fetchCartJson();
    } catch (_) {
      if (!cart) return null;
      workingCart = cart;
    }

    const tiersById = new Map(
      (setting.tiers || []).map((tier) => [String(tier.id), tier])
    );
    let changed = false;

    if (replace) {
      const tierIds = [
        ...new Set(selections.map((s) => String(s.tierId)).filter(Boolean)),
      ];
      for (const tierId of tierIds) {
        const cleared = await removeRewardLinesForTier(workingCart, tierId);
        if (cleared.changed) {
          changed = true;
          workingCart = cleared.cart;
        }
      }
    }

    for (const selection of selections) {
      const tier = tiersById.get(String(selection.tierId));
      if (!tier || tier.rewardType !== REWARD_TIER_TYPE.PRODUCT) continue;
      const product = selection.product;
      const variantId =
        selection.variantId ||
        resolveRewardProductVariantId(product, tier);
      if (!variantId || !product) continue;

      const limit = Math.max(
        1,
        Number(tier.products_to_offer_limit) || 1
      );
      if (countRewardLinesForTier(workingCart, tier.id) >= limit) continue;
      if (rewardLineAlreadyInCart(workingCart, tier.id, variantId)) continue;

      await grantRewardProductLine(
        workingCart,
        tier,
        product,
        variantId,
        trackingContext
      );
      changed = true;
      try {
        workingCart = await fetchCartJson();
      } catch (_) {}
    }

    if (!changed) return null;

    try {
      await syncStorefrontCartUi(null, {
        cartSnapshot: workingCart,
        fetchCart: false,
        refreshDrawer: false,
      });
    } catch (_) {}

    return workingCart;
  });

  rewardSyncChain = task.catch(() => null);
  return task;
}

/**
 * Auto-add / tag reward products when cart crosses tier thresholds,
 * and remove free-gift lines when tiers drop below threshold.
 *
 * Debounced so rapid homepage ATC (2+ quick adds) settles before granting,
 * then serialized via syncRewardProductCartLines to avoid duplicate gifts.
 */
export function useRewardBarRuntime({
  rewardSection,
  cart,
  trackingContext = null,
  enabled = true,
  onCartUpdated,
}) {
  const syncGenRef = useRef(0);
  const syncTimerRef = useRef(null);
  const onCartUpdatedRef = useRef(onCartUpdated);
  const trackingContextRef = useRef(trackingContext);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    onCartUpdatedRef.current = onCartUpdated;
  }, [onCartUpdated]);

  useEffect(() => {
    trackingContextRef.current = trackingContext;
  }, [trackingContext]);

  useEffect(() => {
    if (!enabled || !rewardSection?.setting || !cart) {
      setIsSyncing(false);
      return;
    }

    const setting = rewardSection.setting;
    let cancelled = false;
    const gen = ++syncGenRef.current;

    (async () => {
      try {
        await ensureRewardBarRuntimeContext(setting);
      } catch (_) {}
      if (cancelled || gen !== syncGenRef.current) return;

      const state = computeRewardState(cart, setting);
      const productTiers = state.tiers.filter(
        (tier) => tier.rewardType === REWARD_TIER_TYPE.PRODUCT
      );
      if (!productTiers.length) {
        setIsSyncing(false);
        return;
      }

      const hasStaleRewardLine = (cart.items || []).some((item) => {
        const tierId = getLineRewardTierId(item);
        if (!tierId) return false;
        const tier = productTiers.find(
          (entry) => String(entry.id) === String(tierId)
        );
        if (!tier) return true;
        if (!state.achievedTierIds.has(tierId)) return true;
        const activeVariants = getActiveRewardVariantIdSet(tier);
        return !activeVariants.has(String(item.variant_id || ""));
      });

      const hasDuplicateRewardLine = (() => {
        const seen = new Set();
        for (const item of cart.items || []) {
          const tierId = getLineRewardTierId(item);
          if (!tierId) continue;
          const key = `${tierId}:${item.variant_id}`;
          if (seen.has(key) || Number(item.quantity) > 1) return true;
          seen.add(key);
        }
        return false;
      })();

      const needsSpecificAutoClaim =
        isSpecificProductsRewardMode(setting) &&
        productTiers.some(
          (tier) =>
            state.achievedTierIds.has(tier.id) &&
            tierHasAutoClaimWork(cart, tier)
        );

      const needsGrant = isSpecificProductsRewardMode(setting)
        ? needsSpecificAutoClaim
        : productTiers.some((tier) => {
            if (!state.achievedTierIds.has(tier.id)) return false;
            return getActiveTierRewardProducts(tier).some((product) => {
              const variantId = resolveRewardProductVariantId(product, tier);
              if (!variantId) return false;
              const line = findRewardVariantLine(cart, tier.id, variantId);
              if (!line) return true;
              return !rewardLineHasRevenueTracking(line.properties);
            });
          });

      if (!hasStaleRewardLine && !hasDuplicateRewardLine && !needsGrant) {
        setIsSyncing(false);
        return;
      }

      setIsSyncing(true);
      if (syncTimerRef.current) {
        window.clearTimeout(syncTimerRef.current);
      }
      syncTimerRef.current = window.setTimeout(() => {
        if (cancelled || gen !== syncGenRef.current) return;
        (async () => {
          try {
            const nextCart = await syncRewardProductCartLines(
              cart,
              setting,
              trackingContextRef.current
            );
            if (gen !== syncGenRef.current) {
              if (nextCart && typeof onCartUpdatedRef.current === "function") {
                onCartUpdatedRef.current(nextCart);
              }
              return;
            }
            if (nextCart && typeof onCartUpdatedRef.current === "function") {
              onCartUpdatedRef.current(nextCart);
            } else if (
              needsGrant &&
              typeof onCartUpdatedRef.current === "function"
            ) {
              try {
                const fresh = await fetchCartJson();
                onCartUpdatedRef.current(fresh);
              } catch (_) {}
            }
          } catch (err) {
            console.error("[RewardBar] Failed to sync reward products:", err);
            showCartAddError(err);
          } finally {
            if (gen === syncGenRef.current) setIsSyncing(false);
          }
        })();
      }, 280);
    })();

    return () => {
      cancelled = true;
      if (syncTimerRef.current) {
        window.clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
      }
    };
  }, [rewardSection, cart, enabled]);

  return { isSyncing };
}
